'use client'

import { cn } from '@/lib/utils'
import { useLayoutEffect, useRef } from 'react'

interface AiChatEventProps {
  text: string
  className?: string
}

const EVENT_TEXT_CLASSES = cn(
  'absolute inset-x-0 top-0 block truncate text-main-text-lv03 translate-y-0 opacity-100 blur-none',
  'transition-[translate,filter,opacity] duration-(--think-swap) ease-in-out will-change-[translate,filter,opacity]',
  'data-[state=exit]:-translate-y-2 data-[state=exit]:opacity-0 data-[state=exit]:blur-[2px]',
  'data-[state=enter]:translate-y-2 data-[state=enter]:opacity-0 data-[state=enter]:blur-[2px] data-[state=enter]:transition-none',
  'animate-chat-event-reveal motion-reduce:animate-none motion-reduce:translate-none motion-reduce:filter-none motion-reduce:transition-none',
  'before:absolute before:inset-0 before:pointer-events-none before:truncate before:content-[attr(data-text)]',
  'before:bg-linear-to-r before:from-transparent before:from-40% before:via-main-text-lv02 before:via-50% before:to-transparent before:to-60%',
  'before:bg-size-[400%_100%] before:bg-no-repeat before:bg-clip-text before:text-transparent before:animate-chat-event-shimmer motion-reduce:before:hidden',
)

export function AiChatEvent({ text, className }: AiChatEventProps) {
  const visualRef = useRef<HTMLSpanElement>(null)

  useLayoutEffect(() => {
    const box = visualRef.current
    if (!box) return

    box.querySelectorAll('[data-state="exit"]').forEach((node) => node.remove())
    const leaving = box.querySelector<HTMLElement>('[data-text]')
    if (leaving?.dataset.text === text) {
      void leaving.offsetWidth
      leaving.dataset.state = 'live'
      return
    }

    if (leaving) {
      leaving.dataset.state = 'live'
      void leaving.offsetWidth
      leaving.dataset.state = 'exit'
    }

    const next = document.createElement('span')
    next.className = EVENT_TEXT_CLASSES
    next.textContent = text
    next.dataset.text = text
    next.dataset.state = 'enter'
    box.appendChild(next)

    const computedStyles = getComputedStyle(box)
    const swap = parseFloat(computedStyles.getPropertyValue('--think-swap'))
    const gap = leaving ? parseFloat(computedStyles.getPropertyValue('--think-gap')) : 0
    const release = window.setTimeout(() => {
      void next.offsetWidth
      next.dataset.state = 'live'
    }, gap)
    const cleanup = window.setTimeout(() => leaving?.remove(), swap + gap)

    return () => {
      window.clearTimeout(release)
      window.clearTimeout(cleanup)
    }
  }, [text])

  return (
    <div
      className={cn(
        'relative inline-block w-full overflow-hidden text-left text-ui-body-b2 leading-ui-body-b2 font-medium [--think-swap:150ms] [--think-gap:50ms]',
        className,
      )}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="sr-only">{text}</span>
      <span className="invisible block truncate" aria-hidden="true">
        {text}
      </span>
      <span ref={visualRef} aria-hidden="true" />
    </div>
  )
}
